import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, UserPlus, Users, UserMinus, Clock, Check, X,
  MagnifyingGlass, GameController, CalendarDots, PaperPlaneTilt, Prohibit
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function FriendsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [allPlayers, setAllPlayers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [friendsRes, reqRes] = await Promise.all([
        axios.get(`${API_URL}/api/friends`, { withCredentials: true }),
        axios.get(`${API_URL}/api/friends/requests`, { withCredentials: true }),
      ]);
      setFriends(Array.isArray(friendsRes.data) ? friendsRes.data : []);
      // Backend returns {incoming: [...], outgoing: [...]} — guard against any
      // shape drift so we never crash on requests.incoming.length later.
      const r = reqRes.data || {};
      setRequests({
        incoming: Array.isArray(r.incoming) ? r.incoming : [],
        outgoing: Array.isArray(r.outgoing) ? r.outgoing : [],
      });
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const searchPlayers = async () => {
    if (!search.trim()) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      const friendIds = new Set(friends.map(f => f.id));
      setAllPlayers(data.filter(p => 
        !friendIds.has(p.id) && 
        (p.name.toLowerCase().includes(search.toLowerCase()) || 
         (p.username && p.username.toLowerCase().includes(search.toLowerCase())))
      ));
    } catch (err) { /* silent */ }
  };

  useEffect(() => {
    if (tab === 'add' && search) {
      const timeout = setTimeout(searchPlayers, 300);
      return () => clearTimeout(timeout);
    }
  }, [search, tab]);

  const sendRequest = async (userId) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/friends/request`, { to_user_id: userId }, { withCredentials: true });
      toast.success(data.message);
      if (data.status === 'accepted') fetchData();
      else setAllPlayers(prev => prev.filter(p => p.id !== userId));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const acceptRequest = async (requestId) => {
    try {
      await axios.post(`${API_URL}/api/friends/accept/${requestId}`, {}, { withCredentials: true });
      toast.success('Friend added!');
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const declineRequest = async (requestId) => {
    try {
      await axios.post(`${API_URL}/api/friends/decline/${requestId}`, {}, { withCredentials: true });
      toast.success('Request declined');
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const removeFriend = async (friendId) => {
    try {
      await axios.delete(`${API_URL}/api/friends/${friendId}`, { withCredentials: true });
      toast.success('Friend removed');
      setFriends(prev => prev.filter(f => f.id !== friendId));
    } catch (err) { toast.error('Failed'); }
  };

  // Apple Guideline 1.2 — Block control directly on the Friends list (in addition
  // to user-profile and other-player surfaces). Posts to the existing block
  // endpoint then also unfriends to remove them from local state immediately.
  const blockFriend = async (friend) => {
    if (!window.confirm(`Block ${friend.name}? They won't be matched with you or be able to message you, and they'll be removed from your friends.`)) return;
    try {
      await axios.post(
        `${API_URL}/api/matchmaking/block`,
        { blocked_user_id: friend.id, reason: 'friends_list_initiated' },
        { withCredentials: true }
      );
      try { await axios.delete(`${API_URL}/api/friends/${friend.id}`, { withCredentials: true }); } catch { /* already unfriended */ }
      toast.success(`${friend.name} has been blocked`);
      setFriends(prev => prev.filter(f => f.id !== friend.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not block user');
    }
  };

  const getAvailText = (slots) => {
    if (!slots || slots.length === 0) return null;
    return slots.map(s => `${DAYS[s.day_of_week]}: ${s.time_slots.join(', ')}`).join(' | ');
  };

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';

  return (
    <div className="app-container" data-testid="friends-page">
      <header className="p-6 pb-2 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-white" data-testid="back-button">
          <ArrowLeft size={24} weight="bold" />
        </button>
        <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Friends</h1>
      </header>

      {/* Tabs */}
      <div className="px-6 py-3 flex gap-2">
        {[
          { key: 'friends', label: 'My Friends', icon: Users, count: friends.length },
          { key: 'requests', label: 'Requests', icon: Clock, count: requests.incoming.length },
          { key: 'add', label: 'Add', icon: UserPlus },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex-1 py-2.5 rounded-xl text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
              tab === t.key ? 'bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] text-white' : 'bg-[#181824] text-[#A1A1AA]'
            }`} data-testid={`tab-${t.key}`}>
            <t.icon size={16} weight="duotone" />
            {t.label}
            {t.count > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white/20 text-[10px]">{t.count}</span>}
          </button>
        ))}
      </div>

      <div className="px-6 pb-32 space-y-3">
        <AnimatePresence mode="wait">
          {/* Friends List */}
          {tab === 'friends' && (
            <motion.div key="friends" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {friends.length === 0 ? (
                <div className="text-center py-12 text-[#71717A]">
                  <Users size={48} weight="duotone" className="mx-auto mb-3 text-[#3F3F46]" />
                  <p className="text-sm">No friends yet</p>
                  <p className="text-xs mt-1">Search for players to add!</p>
                </div>
              ) : (
                friends.map((f, i) => (
                  <motion.div key={f.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    className="p-4 rounded-2xl bg-[#181824] border border-white/5 mb-3" data-testid={`friend-card-${i}`}>
                    <div className="flex items-center gap-3">
                      <div onClick={() => navigate(`/user/${f.id}`)} className="cursor-pointer flex items-center gap-3 flex-1 min-w-0">
                        {f.avatar ? (
                          <img src={f.avatar} alt="" className="w-11 h-11 rounded-full object-cover" />
                        ) : (
                          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] flex items-center justify-center text-white text-sm font-bold">{getInitials(f.name)}</div>
                        )}
                        <div className="min-w-0">
                          <p className="text-white font-semibold text-sm truncate">{f.name}</p>
                          <p className="text-[#71717A] text-xs">@{f.username || 'user'} · Lv.{f.level || 0}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => navigate(`/chat/${f.id}`)} className="w-9 h-9 rounded-full bg-[#00F0FF]/10 flex items-center justify-center" data-testid={`msg-friend-${i}`}>
                          <PaperPlaneTilt size={16} weight="fill" className="text-[#00F0FF]" />
                        </button>
                        <button onClick={() => removeFriend(f.id)} className="w-9 h-9 rounded-full bg-yellow-500/10 flex items-center justify-center" data-testid={`remove-friend-${i}`} title="Unfriend">
                          <UserMinus size={16} weight="fill" className="text-yellow-400" />
                        </button>
                        <button onClick={() => blockFriend(f)} className="w-9 h-9 rounded-full bg-red-500/10 flex items-center justify-center" data-testid={`block-friend-${i}`} title="Block">
                          <Prohibit size={16} weight="fill" className="text-red-400" />
                        </button>
                      </div>
                    </div>
                    {/* Availability preview */}
                    {f.availability && f.availability.length > 0 && (
                      <div className="mt-2.5 flex gap-1 flex-wrap">
                        {f.availability.slice(0, 3).map((s, si) => (
                          <span key={si} className="px-2 py-0.5 rounded-md bg-[#39FF14]/10 text-[#39FF14] text-[10px] font-medium">
                            {DAYS[s.day_of_week]}: {s.time_slots.slice(0, 2).join(', ')}
                          </span>
                        ))}
                      </div>
                    )}
                  </motion.div>
                ))
              )}
            </motion.div>
          )}

          {/* Requests */}
          {tab === 'requests' && (
            <motion.div key="requests" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {requests.incoming.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-[#A1A1AA] mb-2 uppercase tracking-wider">Incoming</p>
                  {requests.incoming.map((r, i) => (
                    <div key={r.id} className="p-4 rounded-2xl bg-[#181824] border border-[#9D4CDD]/20 mb-2 flex items-center gap-3" data-testid={`incoming-request-${i}`}>
                      {r.from_user_avatar ? (
                        <img src={r.from_user_avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] flex items-center justify-center text-white text-xs font-bold">{getInitials(r.from_user_name)}</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold text-sm truncate">{r.from_user_name}</p>
                        <p className="text-[#71717A] text-[10px]">wants to be your friend</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => acceptRequest(r.id)} className="w-9 h-9 rounded-full bg-[#39FF14]/20 flex items-center justify-center" data-testid={`accept-request-${i}`}>
                          <Check size={16} weight="bold" className="text-[#39FF14]" />
                        </button>
                        <button onClick={() => declineRequest(r.id)} className="w-9 h-9 rounded-full bg-red-500/10 flex items-center justify-center" data-testid={`decline-request-${i}`}>
                          <X size={16} weight="bold" className="text-red-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {requests.outgoing.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-[#A1A1AA] mb-2 uppercase tracking-wider">Sent</p>
                  {requests.outgoing.map((r, i) => (
                    <div key={r.id} className="p-4 rounded-2xl bg-[#181824] border border-white/5 mb-2 flex items-center gap-3" data-testid={`outgoing-request-${i}`}>
                      <Clock size={20} weight="duotone" className="text-[#FACC15]" />
                      <div className="flex-1">
                        <p className="text-white text-sm">Pending: <span className="font-semibold">{r.to_user_name || 'User'}</span></p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {requests.incoming.length === 0 && requests.outgoing.length === 0 && (
                <div className="text-center py-12 text-[#71717A]">
                  <Clock size={48} weight="duotone" className="mx-auto mb-3 text-[#3F3F46]" />
                  <p className="text-sm">No pending requests</p>
                </div>
              )}
            </motion.div>
          )}

          {/* Add Friends */}
          {tab === 'add' && (
            <motion.div key="add" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="relative mb-4">
                <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717A]" />
                <input type="text" placeholder="Search players by name..." value={search} onChange={(e) => setSearch(e.target.value)}
                  className="input-field pl-11" data-testid="search-players-input" />
              </div>
              {allPlayers.length > 0 ? (
                allPlayers.map((p, i) => (
                  <div key={p.id} className="p-4 rounded-2xl bg-[#181824] border border-white/5 mb-2 flex items-center gap-3" data-testid={`search-result-${i}`}>
                    {p.avatar ? (
                      <img src={p.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#0EA5E9] flex items-center justify-center text-white text-xs font-bold">{getInitials(p.name)}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm truncate">{p.name}</p>
                      <p className="text-[#71717A] text-xs">@{p.username || 'user'} · Lv.{p.level || 0}</p>
                    </div>
                    <button onClick={() => sendRequest(p.id)} className="px-3 py-1.5 rounded-lg bg-[#9D4CDD]/20 text-[#9D4CDD] text-xs font-semibold hover:bg-[#9D4CDD]/30 transition-all" data-testid={`add-friend-${i}`}>
                      <UserPlus size={14} weight="bold" className="inline mr-1" />Add
                    </button>
                  </div>
                ))
              ) : search ? (
                <div className="text-center py-8 text-[#71717A] text-sm">No players found</div>
              ) : (
                <div className="text-center py-12 text-[#71717A]">
                  <MagnifyingGlass size={48} weight="duotone" className="mx-auto mb-3 text-[#3F3F46]" />
                  <p className="text-sm">Search for players to add as friends</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <BottomNav />
    </div>
  );
}
